import type { ComponentType } from "react";
import {
  GraduationCap,
  Home,
  Sparkles,
  History,
  ClipboardList,
  Trophy,
  Star,
  ListChecks,
  Pencil,
  MessagesSquare,
  Tag,
  Shield,
  Users,
  Inbox,
  Upload,
  Download,
} from "lucide-react";

export type Role = "admin" | "manager" | "trainee";

export interface NavItem {
  title: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  /** undefined = visible to everyone authenticated */
  roles?: Role[];
  /** When set, the active-state matcher uses startsWith() for nested routes */
  matchPrefix?: boolean;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
  /** undefined = visible to everyone; group is hidden if all its items are filtered out */
  roles?: Role[];
}

export const NAV: NavGroup[] = [
  {
    title: "Training",
    items: [
      { title: "Categories", to: "/", icon: Home },
      { title: "Train", to: "/train", icon: GraduationCap, matchPrefix: true },
      { title: "Special", to: "/special", icon: Sparkles, matchPrefix: true },
      { title: "My History", to: "/special/history", icon: History },
    ],
  },
  {
    title: "Reviews",
    items: [
      { title: "Dashboard", to: "/reviews", icon: ClipboardList },
      { title: "Best Responses", to: "/reviews/best", icon: Trophy },
      { title: "Pick Best", to: "/reviews/pick", icon: Star, roles: ["admin"] },
      { title: "Pick History", to: "/reviews/pick-history", icon: ListChecks, roles: ["admin"] },
    ],
  },
  {
    title: "Editor",
    roles: ["admin", "manager"],
    items: [
      {
        title: "Conversations",
        to: "/editor/conversations",
        icon: MessagesSquare,
        matchPrefix: true,
        roles: ["admin", "manager"],
      },
      { title: "Categories", to: "/editor/categories", icon: Tag, roles: ["admin"] },
      { title: "Editor Home", to: "/editor", icon: Pencil, roles: ["admin", "manager"] },
    ],
  },
  {
    title: "Admin",
    roles: ["admin"],
    items: [
      { title: "Users", to: "/admin", icon: Users, roles: ["admin"] },
      {
        title: "Chat Imports",
        to: "/admin/imports",
        icon: Download,
        matchPrefix: true,
        roles: ["admin"],
      },
      {
        title: "Special Responses",
        to: "/admin/special",
        icon: Inbox,
        roles: ["admin"],
      },
      {
        title: "Edit Scenarios",
        to: "/admin/special/pending",
        icon: Pencil,
        matchPrefix: true,
        roles: ["admin"],
      },
      {
        title: "Upload Special",
        to: "/admin/special/upload",
        icon: Upload,
        roles: ["admin"],
      },
    ],
  },
];

export function visibleNav(role: Role | undefined | null): NavGroup[] {
  if (!role) return [];
  return NAV.map((group) => {
    if (group.roles && !group.roles.includes(role)) return null;
    const items = group.items.filter((item) => !item.roles || item.roles.includes(role));
    if (items.length === 0) return null;
    return { ...group, items };
  }).filter((g): g is NavGroup => g !== null);
}

export function isItemActive(item: NavItem, pathname: string): boolean {
  if (item.matchPrefix) {
    if (item.to === "/") return pathname === "/";
    return pathname === item.to || pathname.startsWith(item.to + "/");
  }
  return pathname === item.to;
}

export const SHIELD_ICON = Shield;
